/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React, { FC, useMemo, useState } from "react";
import { Box, Typography, Chip } from "@mui/material";
import { AppointmentInterface, VisitStatusEnum } from "@malmur-technologies/medicalsystem-shared";
import moment from "moment";
import { useTranslation } from "react-i18next";
import Button from "components/generals/Button";
import DataTable from "dashboard-template/examples/Tables/DataTable";
import DefaultCell from "components/generals/DefaultCell";
import { CircularProgress } from "@mui/material";
import { TableProps } from "interfaces";

/**
 * Properties for the AppointmentArriveTable component.
 */
interface AppointmentArriveTableProps extends TableProps<AppointmentInterface> {
  appointments: AppointmentInterface[];
  onViewDetails: (appointment: AppointmentInterface) => void;
  onUpdateStatus: (appointmentId: string, newStatus: VisitStatusEnum) => void;
}

/**
 * Appointment arrival table component for front desk/reception workflow.
 * Displays appointments with patient arrival status and time information.
 * 
 * Features:
 * - Displays patient name and phone number
 * - Shows provider (doctor) assigned to appointment
 * - Displays visit reason/chief complaint
 * - Shows arrival date and time in formatted display (DD/MM and HH:mm)
 * - View details button for each appointment
 * - Update appointment status functionality
 * - Compact design for high-density display
 * - Status color coding via Chip components
 * 
 * Use Cases:
 * - Front desk check-in workflow
 * - Tracking patient arrivals throughout the day
 * - Quick access to appointment details
 * - Updating visit status (arrived, in progress, completed)
 * 
 * Security:
 * - Displays only authorized appointment data
 * - Status updates validated by parent component
 * 
 * @param {AppointmentArriveTableProps} props - Component properties
 * @param {AppointmentInterface[]} props.appointments - Array of appointments to display
 * @param {Function} props.onViewDetails - Callback when user clicks view details button
 * @param {Function} props.onUpdateStatus - Callback to update appointment status
 * 
 * @returns {JSX.Element} Appointment arrival table with status management
 */
const AppointmentArriveTable: FC<AppointmentArriveTableProps> = ({
  appointments,
  onViewDetails,
  onUpdateStatus,
  ...props
}) => {
  const { t } = useTranslation();
  const [loadingStatus, setLoadingStatus] = useState<{ [key: string]: VisitStatusEnum | null }>({});

   const handleStatusUpdate = async (appointmentId: string, newStatus: VisitStatusEnum) => {
    setLoadingStatus(prev => ({ ...prev, [appointmentId]: newStatus }));
    try {
      await onUpdateStatus(appointmentId, newStatus);
    } catch (err) {
      console.error("Error updating appointment status:", err);
    } finally {
      setLoadingStatus(prev => ({ ...prev, [appointmentId]: null }));
    }
  };
  const getUITable = (list: AppointmentInterface[]) => {
    return {
      columns: [
        {
          Header: t("appointmentForm.patient"),
          accessor: "patient",
          width: "20%",
          Cell: ({ row }: any) => {
            const appointment = row.original;
            const patientName = appointment.patient?.person
              ? `${appointment.patient.person.name || ""} ${appointment.patient.person.lastname || ""}`.trim()
              : "No Patient";
            const phone = appointment.patient?.person?.phones?.[0]?.number || "";

            return (
              <Box>
                <Typography variant="body2" fontWeight="medium" noWrap fontSize="0.75rem">
                  {patientName}
                </Typography>
                {phone && (
                  <Typography variant="caption" fontSize="0.75rem">
                    {phone}
                  </Typography>
                )}
              </Box>
            );
          },
        },
        {
          Header: t("appointmentForm.doctorProvider"),
          accessor: "provider",
          width: "18%",
          Cell: ({ row }: any) => {
            const appointment = row.original;
            const doctorName = appointment.provider?.person
              ? `${appointment.provider.person.name || ""} ${appointment.provider.person.lastname || ""}`.trim()
              : "No Provider";

            return <DefaultCell value={doctorName} />;
          },
        },
        {
          Header: t("appointmentForm.visitReason"),
          accessor: "reason",
          width: "20%",
          Cell: ({ value }: any) => <DefaultCell value={value || "No reason specified"} />,
        },
        {
          Header: t("appointmentForm.arrivalDateTime"),
          accessor: "arrivalDateTime",
          width: "14%",
          Cell: ({ value }: any) => {
            const appointmentTime = moment(value).format("HH:mm");
            const appointmentDate = moment(value).format("DD/MM");

            return (
              <Box display="flex" flexDirection="column" alignItems="center">
                <Typography variant="body2" fontWeight="medium" fontSize="0.75rem">
                  {appointmentDate}
                </Typography>
                <Typography
                  variant="caption"
                  color="secondary.main"
                  fontWeight="normal"
                  fontSize="0.75rem"
                >
                  {appointmentTime}
                </Typography>
              </Box>
            );
          },
        },
        {
          Header: t("appointmentForm.roomNumber"),
          accessor: "roomNumber",
          width: "14%",
          Cell: ({ value }: any) => (
            <Chip
              label={value || "No room"}
              size="small"
              variant="outlined"
              color={value ? "primary" : "default"}
              sx={{ fontSize: "0.75rem" }}
            />
          ),
        },
        {
          Header: t("appointmentForm.actions"),
          accessor: "id",
          width: "20%",
          Cell: ({ row }: any) => {
            const appointment = row.original;
            const isLoadingCancel = loadingStatus[appointment.id] === VisitStatusEnum.CANCEL;
            const isLoadingNoShow = loadingStatus[appointment.id] === VisitStatusEnum.NO_SHOW;
            
            return (
              <Box display="flex" flexDirection="column" gap={1} alignItems="center">
                <Button
                  variant="contained"
                  color="info"
                  size="small"
                  onClick={() => onViewDetails(appointment)}
                  sx={{
                    minWidth: 100,
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "0.75rem",
                  }}
                >
                  {t("appointmentArriveTable.viewDetails")}
                </Button>

                <Box display="flex" gap={0.5}>
                  <Button
                    name="cy-cancel-appointmet"
                    variant="outlined"
                    size="small"
                    disabled={isLoadingCancel || isLoadingNoShow}
                    onClick={() => handleStatusUpdate(appointment.id, VisitStatusEnum.CANCEL)}
                    startIcon={ isLoadingCancel? <CircularProgress size={14} /> : null }
                  >
                    {t("appointmentArriveTable.cancel")}
                  </Button>

                  <Button
                    name="cy-noShow-appointmet"
                    variant="outlined"
                    size="small"
                    disabled={isLoadingCancel || isLoadingNoShow}
                    onClick={() => handleStatusUpdate(appointment.id, VisitStatusEnum.NO_SHOW)}
                    startIcon={ isLoadingNoShow? <CircularProgress size={14} /> : null }
                  >
                    {t("appointmentArriveTable.noShow")}
                  </Button>
                </Box>
              </Box>
            );
          },
        },
      ],
      rows: list,
    };
  };

  const dataTableData = useMemo(
    () => getUITable(appointments),
    [appointments, onViewDetails, onUpdateStatus, loadingStatus]
  );

  if (appointments.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <Typography variant="body1" color="text.secondary" fontSize="0.75rem">
          {t("appointmentCalendar.noArrivedAppointments")}
        </Typography>
      </Box>
    );
  }

  return <DataTable {...props} table={dataTableData} canSearch />;
};

export default AppointmentArriveTable;
